import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/shared/ui/dialog";
import type { JobPosting } from "@/services/api/job-postings";
import { format } from "date-fns";
import {
  ExternalLink,
  MapPin,
  Building2,
  Globe,
  Calendar,
  CalendarCheck,
} from "lucide-react";

interface JobPostingDetailDialogProps {
  posting: JobPosting | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function JobPostingDetailDialog({
  posting,
  open,
  onOpenChange,
}: JobPostingDetailDialogProps) {
  if (!posting) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="pr-8 leading-snug">
            {posting.title}
          </DialogTitle>
          <DialogDescription asChild>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 pt-1">
              {posting.company && (
                <span className="inline-flex items-center gap-1.5 text-sm text-ink-600">
                  <Building2 className="h-3.5 w-3.5 shrink-0 text-ink-400" />
                  {posting.company}
                </span>
              )}
              {posting.location && (
                <span className="inline-flex items-center gap-1.5 text-sm text-ink-600">
                  <MapPin className="h-3.5 w-3.5 shrink-0 text-ink-400" />
                  {posting.location}
                </span>
              )}
            </div>
          </DialogDescription>
        </DialogHeader>

        {/* Meta grid */}
        <div className="grid grid-cols-2 gap-3 border-y border-grid-200 py-3 shrink-0">
          <MetaItem
            icon={<Globe className="h-3.5 w-3.5" />}
            label="Source"
            value={
              <span className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">
                {posting.sourceName}
              </span>
            }
          />
          <MetaItem
            icon={
              <span
                className={`h-2 w-2 rounded-full shrink-0 ${posting.isActive ? "bg-green-500" : "bg-red-400"}`}
              />
            }
            label="Status"
            value={
              <span
                className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                  posting.isActive
                    ? "bg-green-50 text-green-700"
                    : "bg-red-50 text-red-700"
                }`}
              >
                {posting.isActive ? "Active" : "Inactive"}
              </span>
            }
          />
          <MetaItem
            icon={<Calendar className="h-3.5 w-3.5" />}
            label="First Seen"
            value={format(new Date(posting.firstSeenAt), "MMM d, yyyy")}
          />
          <MetaItem
            icon={<CalendarCheck className="h-3.5 w-3.5" />}
            label="Last Seen"
            value={format(new Date(posting.lastSeenAt), "MMM d, yyyy")}
          />
        </div>

        {/* Description */}
        {posting.description ? (
          <div className="flex-1 min-h-0 overflow-y-auto">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-ink-500 mb-2">
              Description
            </h3>
            <div className="whitespace-pre-line text-sm leading-relaxed text-ink-700">
              {posting.description}
            </div>
          </div>
        ) : (
          <div className="py-6 text-center text-sm text-ink-400">
            No description available
          </div>
        )}

        {/* Footer with external link */}
        {posting.url && posting.isActive && (
          <div className="border-t border-grid-200 pt-3 shrink-0">
            <a
              href={posting.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-md border border-grid-200 bg-white px-4 py-2 text-sm font-medium text-ink-700 shadow-sm transition-colors hover:bg-grid-50 hover:text-ink-900"
            >
              View Original Posting
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function MetaItem({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-2">
      <span className="mt-0.5 text-ink-400">{icon}</span>
      <div className="min-w-0">
        <p className="text-xs text-ink-500">{label}</p>
        <div className="text-sm text-ink-800">{value}</div>
      </div>
    </div>
  );
}
